import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Separator } from "components/core/Seperator";
import { PageTitle } from "components/core/typography/PageTitle";
import { ArchiveCard } from "components/domain/archive/ArchiveCard";
import {
    MixArchiveDocument,
    MixArchiveTagsDocument,
    Order_By,
    SessionDocument,
    useMixArchiveBookmarkedIdsQuery,
    useMixArchiveQuery,
    useMixArchiveTagsQuery
} from "graphql/generated";
import { useAuth } from "hooks/useAuth";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { X } from "lucide-react";
import { GetServerSideProps } from "next";
import React, { useCallback, useMemo, useState } from "react";
import { getClient } from "utils/client";
import { FALLBACK_ARCHIVE_FILES } from "utils/fallback_archive";

export interface ArchiveFile {
    id: string;
    fileName: string;
    fileId: string;
}

// Fallback archive files - used when database is empty
const ArchivePage = () => {
    const { me } = useAuth();
    const { isFeatureEnabled } = useFeatureFlags();

    // Filter state
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    const { data, loading } = useMixArchiveQuery({
        variables: {
            orderBy: [{ created_at: Order_By.Desc }]
        }
    });
    const { data: tagsData } = useMixArchiveTagsQuery();

    // Fetch user's bookmarked archive IDs
    const { data: bookmarkedData } = useMixArchiveBookmarkedIdsQuery({
        variables: {
            userId: me?.id || ""
        },
        skip: !me?.id
    });

    // Create a Set of bookmarked archive IDs for quick lookup
    const bookmarkedArchiveIds = useMemo(
        () =>
            new Set(
                bookmarkedData?.mix_archive_bookmark.map((b) => b.archiveid) ||
                    []
            ),
        [bookmarkedData]
    );

    // Transform fallback data to match database structure
    const fallbackData = useMemo(
        () =>
            FALLBACK_ARCHIVE_FILES.map((file) => ({
                id: file.id,
                title: file.fileName,
                source: "",
                sourcename: "",
                view_url: `https://drive.google.com/file/d/${file.fileId}/view`,
                download_url: `https://drive.google.com/uc?export=download&id=${file.fileId}`,
                thumbnail_url: null,
                tags: null,
                created_at: null,
                updated_at: null,
                bookmarked: false
            })),
        []
    );

    // Use database data if available, otherwise use fallback
    // Add bookmarked status to each archive
    const archiveFiles = useMemo(() => {
        const archives =
            !loading && (data?.mix_archive?.length === 0 || !data?.mix_archive)
                ? fallbackData
                : data?.mix_archive || [];

        return archives.map((archive) => ({
            ...archive,
            bookmarked: bookmarkedArchiveIds.has(archive.id)
        }));
    }, [data?.mix_archive, fallbackData, loading, bookmarkedArchiveIds]);

    const tagEnums = useMemo(
        () => tagsData?.mix_archive_tags || [],
        [tagsData]
    );

    // Tag lookup for Korean display
    const tagLookup = useMemo(
        () =>
            Object.fromEntries(
                tagEnums.map((t) => [t.value, t.comment || t.value])
            ),
        [tagEnums]
    );

    // Available tags (exclude already selected)
    const availableTags = useMemo(
        () => tagEnums.filter((tag) => !selectedTags.includes(tag.value)),
        [tagEnums, selectedTags]
    );

    // Filtered archives
    const filteredArchives = useMemo(() => {
        if (selectedTags.length === 0) {
            return archiveFiles;
        }
        return archiveFiles.filter((archive) =>
            selectedTags.every((tag) => archive.tags?.includes(tag))
        );
    }, [archiveFiles, selectedTags]);

    const { openSignInModal } = useAuth();

    const handleCardClick = useCallback(
        (file: (typeof archiveFiles)[0]) => {
            if (!me) {
                openSignInModal();
                return;
            }
            window.open(file.view_url, "_blank");
        },
        [me, openSignInModal]
    );

    const handleDownload = useCallback(
        (file: (typeof archiveFiles)[0]) => (e: React.MouseEvent) => {
            if (!me) {
                openSignInModal();
                return;
            }
            window.open(file.download_url, "_blank");
        },
        [me, openSignInModal]
    );

    // Filter handlers
    const handleAddTag = useCallback(
        (tagValue: string) => {
            if (tagValue && !selectedTags.includes(tagValue)) {
                setSelectedTags([...selectedTags, tagValue]);
            }
        },
        [selectedTags]
    );

    const handleRemoveTag = useCallback(
        (tagValue: string) => {
            setSelectedTags(selectedTags.filter((tag) => tag !== tagValue));
        },
        [selectedTags]
    );

    const selectContentClassName = cn(
        "bg-[#F5F5F5] text-black dark:bg-[#424242] dark:text-white"
    );

    return (
        <div className="flex flex-col gap-4 md:gap-0">
            {/* Header with Title and Filters */}
            <div
                className="-mt-[0.35rem] mb-6 flex flex-col gap-4 md:flex-row
                    md:items-center md:justify-between"
            >
                <PageTitle className="mb-0 text-foreground dark:text-white">
                    아카이브
                </PageTitle>

                {/* Tag Filter Controls */}
                <div
                    className="flex flex-1 flex-row flex-wrap items-center gap-2
                        md:max-w-2xl md:flex-initial"
                >
                    {/* Selected Tag Badges */}
                    {selectedTags.map((tagValue) => (
                        <Badge
                            key={tagValue}
                            variant="secondary"
                            className={cn(
                                `flex cursor-pointer items-center gap-1.5 py-2
                                transition-colors hover:text-primary
                                hover:opacity-70`,
                                selectContentClassName
                            )}
                        >
                            {tagLookup[tagValue] || tagValue}
                            <X
                                className="h-3 w-3 hover:opacity-70"
                                onClick={() => handleRemoveTag(tagValue)}
                            />
                        </Badge>
                    ))}

                    {/* Tag Select */}
                    <Select value="" onValueChange={handleAddTag}>
                        <SelectTrigger
                            className={cn(
                                "w-full md:w-[200px]",
                                selectContentClassName
                            )}
                        >
                            <SelectValue placeholder="필터 선택" />
                        </SelectTrigger>
                        <SelectContent className={cn(selectContentClassName)}>
                            {availableTags.length === 0 ? (
                                <SelectItem value="__none__" disabled>
                                    모든 태그 선택됨
                                </SelectItem>
                            ) : (
                                availableTags.map((tag) => (
                                    <SelectItem
                                        key={tag.value}
                                        value={tag.value}
                                    >
                                        {tag.comment || tag.value}
                                    </SelectItem>
                                ))
                            )}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {loading ? (
                <div className="py-10 text-center text-muted-foreground">
                    로딩 중...
                </div>
            ) : filteredArchives.length === 0 ? (
                <div className="py-10 text-center text-muted-foreground">
                    {archiveFiles.length === 0
                        ? "아카이브 파일이 없습니다"
                        : "필터 조건에 맞는 아카이브가 없습니다"}
                </div>
            ) : (
                <div
                    className="animate-slidein md:row-gap-6 flex h-full w-full
                        flex-col gap-y-6 md:grid
                        md:grid-cols-[repeat(auto-fill,minmax(270px,1fr))]
                        md:gap-0 md:gap-x-6 md:gap-y-16 md:p-0"
                >
                    {filteredArchives.map((file, index) => (
                        <React.Fragment key={file.id}>
                            <ArchiveCard
                                archive={file}
                                tagEnums={tagEnums}
                                onClick={() => handleCardClick(file)}
                                onDownload={handleDownload(file)}
                                isGuest={!me}
                            />
                            {index < filteredArchives.length - 1 && (
                                <Separator className="md:hidden" />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
    const client = getClient({
        headers: {
            cookie: ctx.req.headers.cookie
        },
        ctx
    });

    try {
        // Pre-fetch archive data and tags for all users (guests and authenticated)
        await Promise.all([
            client.query({
                query: MixArchiveDocument,
                variables: {
                    orderBy: [{ created_at: Order_By.Desc }]
                }
            }),
            client.query({
                query: MixArchiveTagsDocument
            })
        ]);

        return {
            props: {
                __APOLLO_CACHE_DATA__: client.extract()
            }
        };
    } catch (error) {
        // If archive data fetch fails, still allow page access
        return {
            props: {}
        };
    }
};

export default ArchivePage;
